import { z } from "zod";
import { compileExpr } from "./expr.js";
import { FigureSpec } from "./figure.js";
import type { LessonBlock, LessonStep } from "./content.js";

/**
 * Réponses illustrées du tuteur.
 *
 * Après sa réponse en texte, le tuteur peut la transformer en court scénario : quelques étapes jouées
 * par le lecteur de leçon (texte, formule, courbe, erreur fréquente, question rapide).
 * L'IA ne dessine jamais : elle DÉCRIT (« courbe de x^2 - 2x - 3, sommet en x = 1 ») et c'est notre
 * lecteur qui dessine. Tout est vérifié ici avant affichage :
 * - les expressions sont compilées par notre grammaire (pas d'eval) ;
 * - l'ordonnée des points remarquables est recalculée (jamais celle donnée par l'IA) ;
 * - le cadrage de la courbe est choisi par le serveur ;
 * - les formules n'acceptent qu'une liste fermée de commandes ;
 * - une étape fausse est retirée ; sans rien de visuel, pas de scénario (l'élève garde le texte).
 */

export const VISUAL_LIMITS = { steps: 5, itemsPerStep: 3, curves: 3, marks: 4 } as const;

const Str = (n: number) => z.string().trim().min(1).max(n);

/** Ce que le modèle produit (format simple, indépendant du fournisseur). */
export const RawVisualItem = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: Str(300) }),
  z.object({ type: z.literal("formula"), latex: Str(160) }),
  z.object({
    type: z.literal("graph"),
    curves: z.array(z.object({ expr: Str(120), label: z.string().trim().max(40).optional() })).min(1).max(VISUAL_LIMITS.curves),
    marks: z.array(z.object({ x: z.number().finite(), curve: z.number().int().min(0).max(VISUAL_LIMITS.curves - 1).default(0), label: z.string().trim().max(24).optional() })).max(VISUAL_LIMITS.marks).default([]),
    slider: z.object({ name: z.string().regex(/^[a-df-wyzA-Z]$/), label: Str(40), min: z.number().finite(), max: z.number().finite(), initial: z.number().finite() }).optional(),
  }),
  z.object({ type: z.literal("mistake"), text: Str(300) }),
  z.object({ type: z.literal("quiz"), question: Str(200), choices: z.array(Str(80)).min(2).max(4), answer: z.number().int().min(0).max(3), explanation: z.string().trim().max(300).default("") }),
]);
export type RawVisualItem = z.infer<typeof RawVisualItem>;

/** Enveloppe tolérante : chaque élément est revérifié seul (un élément faux n'emporte pas le reste). */
export const RawVisual = z.object({
  steps: z.array(z.object({ title: Str(60), items: z.array(z.unknown()).min(1).max(6) })).min(1).max(8),
});

/** Scénario prêt à jouer : même forme que les blocs et étapes d'une leçon. */
export type TutorVisual = { v: 1; blocks: (LessonBlock & { id: string })[]; steps: LessonStep[] };

/** Commandes LaTeX acceptées (KaTeX tourne de toute façon sans HTML ni lien). */
const LATEX_COMMANDS = new Set([
  "frac", "dfrac", "tfrac", "sqrt", "times", "cdot", "div", "pm", "mp", "le", "leq", "ge", "geq", "neq", "approx", "equiv",
  "infty", "Delta", "delta", "alpha", "beta", "gamma", "pi", "theta", "lambda", "mu", "sigma", "omega", "varphi", "phi",
  "Rightarrow", "Leftrightarrow", "iff", "to", "in", "notin", "mathbb", "left", "right", "quad", "qquad", "text", "mathrm",
  "vec", "overrightarrow", "widehat", "hat", "bar", "overline", "circ", "ldots", "cdots", "dots", "sum", "int", "lim",
  "sin", "cos", "tan", "ln", "log", "exp", "min", "max", "parallel", "perp", "angle", "degree", "prime", "cup", "cap",
]);

export function safeLatex(src: string): boolean {
  if (src.length > 160) return false;
  let depth = 0;
  for (const c of src) { if (c === "{") depth++; else if (c === "}" && --depth < 0) return false; }
  if (depth !== 0) return false;
  for (const m of src.matchAll(/\\([a-zA-Z]+|.)/g)) {
    const cmd = m[1]!;
    if (/^[a-zA-Z]+$/.test(cmd) ? !LATEX_COMMANDS.has(cmd) : !",;:! {}|%".includes(cmd)) return false;
  }
  return true;
}

const niceStep = (span: number) => (span <= 2 ? 0.1 : span <= 20 ? 0.5 : 1);

/** Cadrage choisi par le serveur : autour des points remarquables, sans laisser l'IA écraser la courbe. */
function autoView(fns: ((x: number) => number)[], markPts: { x: number; y: number }[]): FigureSpec["view"] | null {
  let x0 = -5, x1 = 5;
  if (markPts.length) {
    x0 = Math.min(...markPts.map((p) => p.x)) - 3;
    x1 = Math.max(...markPts.map((p) => p.x)) + 3;
    if (x1 - x0 < 6) { const c = (x0 + x1) / 2; x0 = c - 3; x1 = c + 3; }
  }
  x0 = Math.max(x0, -500); x1 = Math.min(x1, 500);
  const ys: number[] = [];
  for (const f of fns) for (let i = 0; i <= 120; i++) { const y = f(x0 + ((x1 - x0) * i) / 120); if (Number.isFinite(y)) ys.push(y); }
  if (!ys.length) return null;
  ys.sort((a, b) => a - b);
  let y0 = ys[Math.floor(ys.length * 0.05)]!, y1 = ys[Math.ceil(ys.length * 0.95) - 1]!;
  for (const p of markPts) { y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); }
  const span = Math.max(y1 - y0, 1);
  if (y0 > 0 && y0 < span) y0 = 0; // l'axe des abscisses reste visible quand il est proche
  if (y1 < 0 && -y1 < span) y1 = 0;
  const pad = Math.max((y1 - y0) * 0.15, 1);
  y0 -= pad; y1 += pad;
  if (y1 - y0 > 1000) return null;
  const r = (v: number) => Math.round(v * 100) / 100;
  return { x: [r(x0), r(x1)], y: [r(y0), r(y1)] };
}

function graphSpec(g: Extract<RawVisualItem, { type: "graph" }>, lang: string): FigureSpec | null {
  const vars = ["x", ...(g.slider ? [g.slider.name] : [])];
  const params: Record<string, number> = g.slider ? { [g.slider.name]: g.slider.initial } : {};
  const curves = g.curves.flatMap((c) => {
    const r = compileExpr(c.expr, vars);
    return r.ok ? [{ ...c, fn: (x: number) => r.expr.fn({ ...params, x }), at: (v: number) => (x: number) => r.expr.fn({ [g.slider!.name]: v, x }) }] : [];
  });
  if (!curves.length) return null;
  // Ordonnées recalculées : un point « sommet (1 ; −4) » faux est corrigé, un point hors de la courbe est retiré.
  const marks = g.marks.flatMap((m) => {
    const c = curves[m.curve];
    const y = c?.fn(m.x);
    return c && Number.isFinite(y) ? [{ curve: m.curve, x: m.x, y: y as number, label: m.label }] : [];
  });
  // Avec un curseur, la courbe doit rester dans le cadre quand l'élève va jusqu'aux bornes.
  const variants = g.slider ? curves.flatMap((c) => [c.at(g.slider!.min), c.at(g.slider!.max)]) : [];
  const view = autoView([...curves.map((c) => c.fn), ...variants], marks);
  if (!view) return null;
  const slider = g.slider && g.slider.max > g.slider.min && g.slider.initial >= g.slider.min && g.slider.initial <= g.slider.max
    ? [{ name: g.slider.name, label: { [lang]: g.slider.label, fr: g.slider.label }, min: g.slider.min, max: g.slider.max, step: niceStep(g.slider.max - g.slider.min), initial: g.slider.initial }]
    : [];
  if (g.slider && !slider.length) return null;
  const COLORS = ["teal", "amber", "ink"] as const;
  const spec = {
    kind: "function-plot" as const,
    view,
    sliders: slider,
    curves: curves.map((c, i) => ({ expr: c.expr, color: COLORS[i]!, style: "solid" as const, label: c.label })),
    point: null,
    marks: marks.map(({ curve, x, label }) => ({ curve, x, label })),
  };
  const ok = FigureSpec.safeParse(spec);
  return ok.success ? ok.data : null;
}

/** Transforme ce que le modèle a produit en scénario vérifié ; null s'il ne reste rien de visuel. */
export function buildTutorVisual(raw: unknown, lang = "fr"): { visual: TutorVisual; dropped: number } | null {
  const env = RawVisual.safeParse(raw);
  if (!env.success) return null;
  const blocks: TutorVisual["blocks"] = [];
  const steps: LessonStep[] = [];
  let dropped = 0;
  let lastFigure: string | null = null;
  let visual = 0;
  for (const st of env.data.steps) {
    if (steps.length >= VISUAL_LIMITS.steps) { dropped += st.items.length; continue; }
    const ids: string[] = [];
    let hasFigure = false;
    for (const rawItem of st.items) {
      const p = RawVisualItem.safeParse(rawItem);
      if (!p.success || ids.length >= VISUAL_LIMITS.itemsPerStep) { dropped++; continue; }
      const it = p.data;
      const id = `b${blocks.length + 1}`;
      let block: LessonBlock | null = null;
      if (it.type === "text") block = { type: "text", md: it.text };
      else if (it.type === "mistake") block = { type: "mistake", md: it.text };
      else if (it.type === "formula") block = safeLatex(it.latex) ? { type: "formula", latex: it.latex, display: true } : null;
      else if (it.type === "quiz") {
        block = it.answer < it.choices.length
          ? { type: "quiz", question: it.question, choices: it.choices.map((label, i) => ({ id: "abcd"[i]!, label })), answerId: "abcd"[it.answer]!, explanation: it.explanation }
          : null;
      } else {
        const spec = graphSpec(it, lang);
        block = spec ? { type: "figure", assetId: "tutor", engine: "mafs", spec: spec as unknown as Record<string, unknown>, params: {} } : null;
        if (block) { hasFigure = true; lastFigure = id; }
      }
      if (!block) { dropped++; continue; }
      if (block.type !== "text") visual++;
      blocks.push({ ...block, id });
      ids.push(id);
    }
    if (!ids.length) continue;
    // La courbe reste à l'écran dans les étapes suivantes (on la commente, on ne la redessine pas).
    if (!hasFigure && lastFigure) ids.unshift(lastFigure);
    steps.push({ id: `s${steps.length + 1}`, title: { [lang]: st.title, fr: st.title }, blockIds: ids });
  }
  if (!visual || !steps.length) return null;
  return { visual: { v: 1, blocks, steps }, dropped };
}

/** Lecture d'un scénario stocké (forme minimale vérifiée ; null sinon). */
export function readTutorVisual(raw: unknown): TutorVisual | null {
  const r = z.object({ v: z.literal(1), blocks: z.array(z.record(z.unknown())).min(1).max(20), steps: z.array(z.record(z.unknown())).min(1).max(VISUAL_LIMITS.steps) }).safeParse(raw);
  return r.success ? (raw as TutorVisual) : null;
}
